import * as React from 'react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { periodFromParams } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/periodFromParams'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import type { GroupType, ViewType } from 'modules/shift_management/store/internal/models/View'
import View from 'modules/shift_management/store/internal/models/View'
import {
  DEFAULT_GROUP_TYPE,
  DEFAULT_VIEW_TYPE,
  isValidGranularityViewType,
  isValidGroupType,
  isValidViewType,
  transformGranularityViewTypeToViewType,
} from 'modules/shift_management/store/internal/models/View'

import { useEnabledFeatures } from './useEnabledFeatures'
import useIsTimeFormat24H from './useIsTimeFormat24H'

const useChangeView = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [, dispatchSchedule] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchPagination] = usePagination()
  const [, dispatchFilters] = useFilters()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryflowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')

  const localStorageKey = LOCAL_STORAGE_KEYS.shiftManagementView
  const [localStorageView, setLocalStorageView] = useLocalStorage<string>(localStorageKey)

  const params = useParams<{
    viewType: ViewType
    groupType: GroupType
    year: string
    month: string
    day: string
  }>()

  // If URL params are missing, try to use localStorage route
  const getViewTypeFromStorage = useCallback(
    (isNewEntryflowEnabled: boolean): ViewType => {
      if (params.viewType) return params.viewType

      if (localStorageView) {
        const splitRoute = localStorageView.split('/')
        if (isNewEntryflowEnabled) {
          const granularity = splitRoute[0]
          if (isValidGranularityViewType(granularity)) {
            return transformGranularityViewTypeToViewType(granularity)
          }
        } else {
          const viewTypeValue = splitRoute[0]
          if (isValidViewType(viewTypeValue)) {
            return viewTypeValue
          }
        }
      }

      return DEFAULT_VIEW_TYPE
    },
    [params.viewType, localStorageView]
  )

  const getGroupTypeFromStorage = useCallback((): GroupType => {
    if (params.groupType) return params.groupType

    if (localStorageView) {
      const splitRoute = localStorageView.split('/')
      const groupTypeValue = splitRoute[1]
      if (isValidGroupType(groupTypeValue)) {
        return groupTypeValue
      }
    }

    return DEFAULT_GROUP_TYPE
  }, [params.groupType, localStorageView])

  const viewType: ViewType = getViewTypeFromStorage(newEntryflowEnabled)
  const groupType: GroupType = getGroupTypeFromStorage()
  const dateFromParams = periodFromParams(params, viewType)
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const view = new View({
    date: dateFromParams,
    viewType,
    groupType,
    isTimeFormat24H,
  })
  const [routeOption, setRouteOption] = React.useState<string>(`${viewType}/${groupType}`)

  useEffect(() => {
    const routeViewType = routeOption.split('/')[0]
    if (routeViewType !== params.viewType) {
      const newRoute = `${params.viewType}/${view.groupType}`
      setRouteOption(newRoute)
      setLocalStorageView(newRoute)
    }
  }, [params.viewType]) // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeRoute = useCallback(
    (newRoute: string) => {
      const splitRoute = newRoute.split('/')
      let viewTypeValue: ViewType

      if (newEntryflowEnabled) {
        viewTypeValue = isValidGranularityViewType(splitRoute[0])
          ? transformGranularityViewTypeToViewType(splitRoute[0])
          : DEFAULT_VIEW_TYPE
      } else {
        viewTypeValue = isValidViewType(splitRoute[0]) ? splitRoute[0] : DEFAULT_VIEW_TYPE
      }

      const groupTypeValue: GroupType = isValidGroupType(splitRoute[1])
        ? splitRoute[1]
        : DEFAULT_GROUP_TYPE

      setRouteOption(newRoute)
      setLocalStorageView(newRoute)

      const newDateFromParams = periodFromParams(params, viewTypeValue)
      const newView = new View({
        date: newDateFromParams,
        viewType: viewTypeValue,
        groupType: groupTypeValue,
        isTimeFormat24H,
      })

      track(trackingEvents.shiftManagement.selectShiftManagementView, {
        view: newView.viewType,
        view_type_by: newView.groupType,
        from: 'dropdown_list',
      })

      navigate(
        navigation.shifts.customViewGroup.fullPath({
          viewType: newView.viewType,
          groupType: newView.groupType,
          year: newView.startRangeYear,
          month: newView.startRangeMonth,
          day: newView.startRangeDay,
        })
      )

      dispatchUI({ type: 'LOADING' })
      dispatchSchedule({ type: 'RESET_VIEW', view: newView })
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      dispatchFilters({ type: 'RESET_SHIFT_IDS' })
      dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
      dispatchPagination({ type: 'RESET_PAGINATION' })
    },
    [
      newEntryflowEnabled,
      params,
      isTimeFormat24H,
      navigate,
      navigation,
      setLocalStorageView,
      dispatchUI,
      dispatchSchedule,
      dispatchSelection,
      dispatchFilters,
      dispatchShiftsRows,
      dispatchPagination,
    ]
  )

  const onChangePeriod = (newView: View) => {
    navigate(
      navigation.shifts.customViewGroup.fullPath({
        viewType: newView.viewType,
        groupType: newView.groupType,
        year: newView.startRangeYear,
        month: newView.startRangeMonth,
        day: newView.startRangeDay,
      })
    )

    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view: newView })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  return { view, onChangeRoute, onChangePeriod }
}

export default useChangeView
